import moment from "moment";
import { FormSubmissionSubmittedBy, PatientDto } from "../../../api/web-api-client";
import IComponent from "../../../services/forms.interface";
import { SIGNATURE } from "../shared/constants";

export const removeSignatureRowsFromLayout = (
    layout: any[],
    components: Map<string, IComponent>,
    submittedBy: FormSubmissionSubmittedBy
): any[] => {
    if (!Array.isArray(layout) || !components) return layout;

    return layout.filter(row => {
        // Check all columns in the row
        return !row.children.some(column =>
            Array.isArray(column.children) &&
            column.children.some(child => {
                const comp = components[child.id];
                if (!comp) return false;
                if (comp.type !== SIGNATURE) return false;

                if (submittedBy === FormSubmissionSubmittedBy.Practice) {
                    // Hide all signature components for Practice
                    return true;
                }
                if (submittedBy === FormSubmissionSubmittedBy.Patient) {
                    // Hide only signature components with practice === true for Patient
                    return comp.practice === true;
                }
                return false;
            })
        );
    });
};

export const applySubmissionJsonToComponents = (components: Map<string, IComponent>, submissionJson: { id?: string; fieldId?: number; value: any }[]): Map<string, IComponent> => {
    if (!components || !submissionJson) return components;

    // Build lookups for id and fieldId
    const valueById = new Map<string, any>();
    const valueByFieldId = new Map<number, any>();
    submissionJson.forEach(item => {
        if (item && item.id) valueById.set(item.id, item.value);
        if (item && typeof item.fieldId === 'number') valueByFieldId.set(item.fieldId, item.value);
    });

    // Helper to update a component and its children
    const updateComponent = (component: IComponent) => {
        if (!component || component.value) return;

        if (component.type === SIGNATURE) return; // Exclude SIGNATURE components

        // Prefer id match, fallback to fieldId match
        if (valueById.has(component.id)) {
            component.value = valueById.get(component.id);
        } else if (typeof component.fieldId === 'number' && valueByFieldId.has(component.fieldId)) {
            component.value = valueByFieldId.get(component.fieldId);
        }

        if (component.childComponents) {
            Object.values(component.childComponents).forEach(child => updateComponent(child));
        }
    };

    Object.values(components).forEach(component => updateComponent(component));

    return components;
};

export const preFillFormWithPatientData = (target: Map<string, IComponent>, selectedPatient: PatientDto): Map<string, IComponent> => {

    if (!target || !selectedPatient)
        return target;

    const updateComponentValue = (component: IComponent, value: string) => {
        if (component && value) {
            component.value = value;
        }
    };

    const responsibleParty = selectedPatient.isResponsibleParty ? selectedPatient : selectedPatient.familyMembers?.find(member => member.isResponsibleParty);

    const fieldValueMapping = {
        5689: selectedPatient?.firstName ?? '',
        5687: selectedPatient?.lastName ?? '',
        5686: selectedPatient.dateOfBirth ?? '',
        5683: selectedPatient?.email ?? '',
        5725: selectedPatient?.cellPhone ?? '',
        5726: selectedPatient?.homePhone ?? '',
        5707: selectedPatient?.workPhone ?? '',
        5682: selectedPatient?.address?.address1 ?? '',
        5691: selectedPatient?.address?.address2 ?? '',
        5703: selectedPatient?.address?.city ?? '',
        5704: selectedPatient?.address?.state ?? '',
        5705: selectedPatient?.address?.zip ?? '',
        5681: selectedPatient?.isResponsibleParty ? "0" : "1",
        5657: selectedPatient?.insuranceInfo?.memberId ?? '',
        5794: selectedPatient?.insuranceInfo?.subscriberDateOfBirth ?? '',
        5796: selectedPatient?.insuranceInfo?.subscriberLastName ?? '',
        5797: selectedPatient?.insuranceInfo?.subscriberFirstName ?? '',
        5658: selectedPatient?.insuranceInfo?.carrierId ?? selectedPatient?.insurance,
        5680: responsibleParty?.firstName ?? '',
        5678: responsibleParty?.lastName ?? '',
        5651: responsibleParty?.dateOfBirth ?? '',
        5647: responsibleParty?.email ?? '',
        5648: responsibleParty?.cellPhone ?? '',
        5649: responsibleParty?.homePhone ?? '',
        5639: responsibleParty?.workPhone ?? '',
        5645: responsibleParty?.address?.address1 ?? '',
        5644: responsibleParty?.address?.address2 ?? '',
        5653: responsibleParty?.address?.city ?? '',
        5643: responsibleParty?.address?.state ?? '',
        5641: responsibleParty?.address?.zip ?? '',

    };

    const mapPatientByFieldId = (component: IComponent) => {

        if (!responsibleParty)
            return;

        let value = fieldValueMapping[component.fieldId];

        // Ensure value is a string before trimming
        if (typeof value === 'string') {
            value = value.trim();
        }

        updateComponentValue(component, value);

        if (component.childComponents) {
            Object.values(component.childComponents).forEach((childComponent) => {
                mapPatientByFieldId(childComponent);
            });
        }
    }

    const mapPatientByLabel = (component: IComponent) => {
        switch (component.label?.toLowerCase()) {
            case "first name":
                updateComponentValue(component, selectedPatient.firstName);
                break;
            case "last name":
                updateComponentValue(component, selectedPatient.lastName);
                break;
            case "date of birth":
                if (selectedPatient.dateOfBirth)
                    updateComponentValue(component, moment(selectedPatient.dateOfBirth).format('MM/DD/yyyy'));
                break;
            case "email":
                updateComponentValue(component, selectedPatient.email);
                break;
            case "phone":
                updateComponentValue(component, selectedPatient.phone);
                break;
            // Add more cases as needed for other fields
            default:
                break;
        }
    };

    const hasFieldId = Object.values(target).some(obj => 'fieldId' in obj);

    for (const key in target) {
        if (hasFieldId) {
            mapPatientByFieldId(target[key]);
        } else {
            mapPatientByLabel(target[key]);
        }
    }

    return target;
};

export const regex = (mask) => {
    switch (mask) {
        case 'maskZipUs': {
            return /^\d{5}$|^\d{5}-\d{4}$/;
        }
        case 'maskSSN': {
            return /^\(?\d{3}\)?[- ](\d{2})[- ](\d{4})$/;
        }
        case 'maskPhoneUs': {
            return /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
        }
        case 'maskState': {
            return /[a-zA-Z][a-zA-Z]/;
        }
        case 'maskEmail': {
            return /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
        }

    }
}
